import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Button,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Paper,
  Alert,
  Chip,
  InputAdornment,
  IconButton,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import { loginUser } from "../utils/api";
import { useSelector, useDispatch } from "react-redux";
import { loginSuccess, loginFailure } from "../features/authSlice";

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    username: location.state?.username || "",
    password: location.state?.password || "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setError("");
    setIsLoading(true);
    try {
      const data = await loginUser(formData);
      dispatch(loginSuccess(data.user));
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid username or password.");
      dispatch(loginFailure());
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = () => {
    setFormData({ username: "james_smith", password: "Password123!" });
  };

  if (isAuthenticated) {
    navigate("/");
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 55%, #0F3460 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: "20px",
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Brand header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)",
            py: 4,
            px: 4,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: "50%",
              bgcolor: "rgba(212,175,55,0.15)",
              border: "2px solid rgba(212,175,55,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <LockOutlinedIcon sx={{ color: "#D4AF37", fontSize: 26 }} />
          </Box>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 800,
              background: "linear-gradient(90deg, #fff 10%, #D4AF37 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-0.5px",
            }}
          >
            HasanPulse
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 13.5, mt: 0.5 }}>
            Sign in to your account
          </Typography>
        </Box>

        {/* Form body */}
        <Box sx={{ px: 4, py: 4, bgcolor: "#fff" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "10px" }}>
              {error}
            </Alert>
          )}

          <Chip
            label="Fill demo credentials"
            onClick={handleDemoLogin}
            size="small"
            sx={{
              mb: 3,
              cursor: "pointer",
              bgcolor: "rgba(212,175,55,0.08)",
              color: "#b8941f",
              border: "1px solid rgba(212,175,55,0.35)",
              fontWeight: 600,
              fontSize: 12,
              "&:hover": { bgcolor: "rgba(212,175,55,0.18)" },
            }}
          />

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ color: "#9CA3AF", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small">
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 18, color: "#9CA3AF" }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18, color: "#9CA3AF" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              size="large"
              fullWidth
              sx={{
                mt: 0.5,
                py: 1.4,
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: 15,
                textTransform: "none",
                background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)",
                boxShadow: "0 4px 14px rgba(26,26,46,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #D4AF37 0%, #b8941f 100%)",
                  color: "#1A1A2E",
                  boxShadow: "0 4px 14px rgba(212,175,55,0.4)",
                },
              }}
            >
              {isLoading ? <CircularProgress size={22} color="inherit" /> : "Sign In"}
            </Button>
          </form>

          <Typography sx={{ mt: 3, textAlign: "center", color: "#6B7280", fontSize: 14 }}>
            Don't have an account?{" "}
            <Link
              to="/register"
              style={{ color: "#D4AF37", fontWeight: 700, textDecoration: "none" }}
            >
              Create one
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "&:hover fieldset": { borderColor: "#D4AF37" },
    "&.Mui-focused fieldset": { borderColor: "#1A1A2E" },
  },
  "& label.Mui-focused": { color: "#1A1A2E" },
};

export default Login;
